import { useEffect, type ReactNode } from 'react';

/**
 * Right-side drill-in sheet.
 *
 * Markup mirrors the prototype exactly — `.scrim` + `.drawer` with `.dh`
 * (`.k` eyebrow, `h3`, `.dmeta` pill row, `.dclose`), `.db` body, `.df`
 * footer. The `on` class drives the 240ms slide, so the element stays mounted
 * and animates rather than popping in.
 *
 * Closes on scrim click, the ✕, or Escape — all three, because a sheet that
 * traps you is worse than no sheet.
 */
export function Drawer({
  open,
  eyebrow,
  title,
  meta,
  children,
  footer,
  onClose,
}: {
  open: boolean;
  eyebrow: string;
  title: string;
  meta?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <>
      <div className={`scrim${open ? ' on' : ''}`} onClick={onClose} />
      <aside className={`drawer${open ? ' on' : ''}`} role="dialog" aria-label={title} aria-hidden={!open}>
        <div className="dh" style={{ position: 'relative' }}>
          <div className="k">{eyebrow}</div>
          <h3>{title}</h3>
          {meta && <div className="dmeta">{meta}</div>}
          <button className="dclose" onClick={onClose} aria-label="Close" type="button">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="db">{children}</div>
        {footer && <div className="df">{footer}</div>}
      </aside>
    </>
  );
}

/** A labelled section inside the drawer body. */
export function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="dsec">
      <div className="dl">{label}</div>
      {children}
    </div>
  );
}

/** Note block. `danger` is for open findings. */
export function Note({ danger, children }: { danger?: boolean; children: ReactNode }) {
  return <div className={`note${danger ? ' bad' : ''}`}>{children}</div>;
}

export type KV = [string, ReactNode];

/** Key-value list, 118px label column. Empty values read as an em-dash. */
export function KeyValues({ rows }: { rows: KV[] }) {
  return (
    <dl className="kv">
      {rows.map(([k, v]) => (
        <div key={k} style={{ display: 'contents' }}>
          <dt>{k}</dt>
          <dd>{v === null || v === undefined || v === '' ? <span className="na">—</span> : v}</dd>
        </div>
      ))}
    </dl>
  );
}

export interface Step {
  title: string;
  sub?: string;
  state: 'done' | 'now' | 'future';
}

/** Response timeline. Service actions belong here too, not just analyst ones. */
export function Steps({ steps }: { steps: Step[] }) {
  return (
    <div className="steps">
      {steps.map((s, i) => (
        <div className={`step${s.state === 'done' ? ' done' : s.state === 'now' ? ' now' : ''}`} key={`${s.title}-${i}`}>
          <span className="sd" />
          <span>
            <span className="st">{s.title}</span>
            {s.sub && <span className="ss">{s.sub}</span>}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Cross-link row — navigation, not an action.
 *
 * These are the links the handoff requires to survive the port: framework →
 * filtered Controls, control ↔ its deadlines, deadline → its control, incident
 * → its source detection, request → its linked signal.
 */
export function CrossLink({
  tag,
  label,
  onOpen,
}: {
  tag: string;
  label: string;
  onOpen: () => void;
}) {
  return (
    <button className="dlink" onClick={onOpen} type="button">
      <span className="dlk">{tag}</span>
      {label}
    </button>
  );
}

/**
 * Footer actions.
 *
 * Every action in the handoff's drawer spec is a WRITE (Mark tested, Close
 * incident, Approve, Record disposition). No write endpoints exist yet, so the
 * buttons render disabled above a line saying why, rather than looking live and
 * doing nothing. A button that silently fails teaches people to distrust the
 * screens that do work.
 */
export function Actions({ labels }: { labels: string[] }) {
  return (
    <>
      <div className="cw-nowrite">
        Read-only for now — these actions ship with the write API.
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {labels.map((l, i) => (
          <button
            key={l}
            className={`btn ${i === labels.length - 1 ? 'btn-p' : 'btn-o'}`}
            disabled
            type="button"
            title="Write actions are not enabled yet"
          >
            {l}
          </button>
        ))}
      </div>
    </>
  );
}
